import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { AnimatePresence, motion } from 'motion/react'
import { create } from '@bufbuild/protobuf'
import {
  useMutation,
  useQuery as useConnectQuery,
} from '@connectrpc/connect-query'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useIsFetching } from '@tanstack/react-query'

import {
  GetWaitlistStatusResponseSchema,
  WaitlistStatus_Status,
  WaitlistStatusSchema,
} from '@jotta/grpc-connect-openapi/search'
import {
  getWaitlistStatus,
  joinWaitlist,
} from '@jotta/grpc-connect-openapi/searchQuery'
import { useSetOnboarding } from '@jotta/grpc-js-client/onboarding'
import { useMediaQuery } from '@jotta/hooks/useMediaQuery'
import { cn } from '@jotta/utils/css'

import Spinner from '../Elements/ProgressIndicator/Circular/Spinner/Spinner'
import SvgSearch from '../generated/icons/SvgSearch'
import SvgX from '../generated/icons/SvgX'
import SvgXCircle from '../generated/icons/SvgXCircle'
import { PlainBrandIcon } from '../Icons/BrandIcon/BrandIcon'
import { Breakpoint } from '../themes/breakpoints'
import { showToast } from '../Toasts/ToastProvider'

type SearchType = 'files' | 'photos'
export interface SearchProps {
  onSubmit?: (query: string) => void
  onOpenChange?: (open: boolean) => void
  open?: boolean
  initialQuery?: string
  type: SearchType
}

type SearchFormValues = {
  q: string
}

function getSearchPlaceholderText(
  searchType: SearchType,
  didActivate: boolean,
) {
  if (didActivate) {
    return t`Try typing "sunset" or "dog" to explore`
  }
  return searchType === 'files'
    ? t`Search for files and documents`
    : t`Search for photos and videos`
}

export function Search({ onSubmit, open, initialQuery, type }: SearchProps) {
  const hasInitialQuery = Boolean(
    initialQuery && initialQuery.trim().length > 0,
  )
  const maxMd = useMediaQuery(`(max-width: ${Breakpoint.lg - 1}px)`)

  const [searchType] = useState<SearchProps['type']>(type)
  const [showClearButton, setShowClearButton] = useState(hasInitialQuery)
  const [didActivatePhotoSearch, setDidActivatePhotoSearch] = useState(false)
  const [showSearchInput, setShowSearchInput] = useState(
    maxMd ? hasInitialQuery : true,
  )
  const { data: waitlistStatus, refetch: refetchWaitlistStatus } =
    useConnectQuery(
      getWaitlistStatus,
      {},
      {
        placeholderData: () =>
          create(GetWaitlistStatusResponseSchema, {
            status: create(WaitlistStatusSchema, {
              status: WaitlistStatus_Status.ACTIVE,
              waitlistPosition: 0,
            }),
          }),
        select: data => data?.status?.status,
        enabled: type === 'photos',
      },
    )

  const joinWaitlistMutation = useMutation(joinWaitlist, {
    onError: () => {
      showToast({
        message: t`Failed to activate photo search`,
        hasCloseButton: true,
      })
    },
    onSuccess: () => {
      void refetchWaitlistStatus()
    },
  })
  const isSearching = useIsFetching({ queryKey: ['file_search'] }) > 0

  const inputRef = useRef<HTMLInputElement | null>(null)
  const openedByUserRef = useRef<boolean>(false)
  const hasMountedRef = useRef(false)
  const prevWaitlistStatusRef = useRef<WaitlistStatus_Status | undefined>(
    undefined,
  )

  useLayoutEffect(() => {
    if (!showSearchInput) {
      return
    }
    if (openedByUserRef.current || hasInitialQuery) {
      inputRef.current?.focus()
      openedByUserRef.current = false
    }
  }, [showSearchInput, hasInitialQuery])

  useEffect(() => {
    if (open === true) {
      setShowSearchInput(true)
    }
    if (open === false && maxMd) {
      setShowSearchInput(false)
    }
  }, [open, maxMd])

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      prevWaitlistStatusRef.current = waitlistStatus
      return
    }

    if (
      prevWaitlistStatusRef.current !== WaitlistStatus_Status.ACTIVE &&
      waitlistStatus === WaitlistStatus_Status.ACTIVE
    ) {
      setDidActivatePhotoSearch(true)
    }

    prevWaitlistStatusRef.current = waitlistStatus
  }, [waitlistStatus])

  const { register, handleSubmit, reset } = useForm<SearchFormValues>({
    defaultValues: { q: initialQuery ?? '' },
  })
  const qRegister = register('q')

  useEffect(() => {
    // Reset form when initialQuery changes (e.g., when navigating away from search)
    reset({ q: initialQuery ?? '' })
    setShowClearButton(hasInitialQuery)
  }, [initialQuery, reset, hasInitialQuery])

  function submit(values: SearchFormValues) {
    const q = values.q.trim()
    if (!q) {
      return
    }
    onSubmit?.(values.q)
    setShowClearButton(true)
  }

  if (
    searchType === 'photos' &&
    waitlistStatus !== WaitlistStatus_Status.ACTIVE
  ) {
    return (
      <SearchContainer
        open={showSearchInput}
        onOpenChange={open => setShowSearchInput(open)}
      >
        <InactivePhotoSearch
          waitlistStatus={waitlistStatus}
          onJoinWaitlist={() => joinWaitlistMutation.mutateAsync({})}
        />
      </SearchContainer>
    )
  }

  return (
    <SearchContainer
      open={showSearchInput}
      onOpenChange={open => setShowSearchInput(open)}
    >
      <form
        className={cn(
          'flex w-full min-w-0 items-center gap-1 rounded-full bg-primary lg:focus-within:shadow-sm lg:focus:shadow-sm',
          'outline-none outline-offset-0 [&:has(input:focus-visible)]:outline-2 [&:has(input:focus-visible)]:outline-m3-primary',
        )}
        role="search"
        onSubmit={handleSubmit(submit)}
      >
        <div className="flex min-w-0 flex-1 items-center">
          <label htmlFor="search-input" className="sr-only">
            {getSearchPlaceholderText(searchType, didActivatePhotoSearch)}
          </label>

          <input
            id="search-input"
            type="search"
            inputMode="search"
            role="searchbox"
            placeholder={getSearchPlaceholderText(
              searchType,
              didActivatePhotoSearch,
            )}
            className="body-sm-sh min-w-0 flex-1 rounded-full bg-transparent py-3 pl-5 pr-2 outline-none"
            {...qRegister}
            onKeyDown={e => {
              if (e.key === 'Escape') {
                e.stopPropagation()
              } else if (e.currentTarget.value) {
                setShowClearButton(true)
              }
            }}
            ref={el => {
              qRegister.ref(el)
              inputRef.current = el
            }}
          />

          <button
            type="button"
            className="shrink-0 rounded-full outline-none outline-offset-0 focus-visible:outline-2 focus-visible:outline-m3-primary"
            onClick={() => {
              reset({ q: '' })
              setShowClearButton(false)
              inputRef.current?.focus()
            }}
            aria-hidden={!showClearButton}
            disabled={!showClearButton}
          >
            <PlainBrandIcon
              icon={SvgXCircle}
              className={cn(
                'size-[24px] lg:size-[18px]',
                !showClearButton && maxMd && 'hidden',
                !showClearButton && !maxMd && 'invisible',
              )}
            />
          </button>
        </div>

        <SearchButton
          searchType={searchType}
          isLoading={searchType === 'files' && isSearching}
        />
      </form>
    </SearchContainer>
  )
}

function SearchButton({
  searchType,
  isLoading = false,
}: {
  searchType: SearchType
  isLoading?: boolean
}) {
  return (
    <div className="shrink-0 rounded-full border-none bg-transparent p-1">
      <button
        type="submit"
        className="btn btn-tonal flex min-h-min items-center justify-center rounded-full border-none p-1.5 lg:gap-3"
      >
        <span className="body-sm-sh inline pl-2">
          {searchType === 'photos' && <Trans>Photos</Trans>}
          {searchType === 'files' && <Trans>Files</Trans>}
        </span>

        {isLoading ? (
          <Spinner
            primaryStrokeClassName="stroke-informative"
            secondaryStrokeClassName="stroke-on-primary"
          />
        ) : (
          <PlainBrandIcon icon={SvgSearch} />
        )}
      </button>
    </div>
  )
}

function SearchContainer({
  children,
  onOpenChange,
  open,
}: {
  children: React.ReactNode
  onOpenChange: (open: boolean) => void
  open: boolean
}) {
  return (
    <div className={cn('relative inline-block lg:block lg:w-full')}>
      {open ? (
        <div className="fixed left-0 right-0 top-0 z-appHeader flex items-center gap-2 bg-document p-4 lg:relative lg:z-auto lg:justify-between lg:bg-transparent lg:p-0">
          {children}

          <button
            className="btn btn-elevated block shrink-0 lg:hidden"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            <SvgX height={24} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          aria-label="Search"
          onClick={() => onOpenChange(true)}
          className="btn btn-elevated"
        >
          <PlainBrandIcon icon={SvgSearch} width={24} />
        </button>
      )}
    </div>
  )
}

function InactivePhotoSearch({
  waitlistStatus,
  onJoinWaitlist,
}: {
  waitlistStatus: WaitlistStatus_Status | undefined
  onJoinWaitlist: () => void
}) {
  const animationDuration = 0.2
  const setOnboarding = useSetOnboarding()

  return (
    <AnimatePresence mode="popLayout">
      {waitlistStatus !== WaitlistStatus_Status.ON_WAITLIST ? (
        <motion.div
          key="activate"
          layoutId="activate"
          className="flex grow items-center self-center rounded-full bg-primary"
          exit={{ opacity: 0 }}
          transition={{ duration: animationDuration }}
        >
          <span className="body-sm-sh grow overflow-hidden text-ellipsis whitespace-nowrap pl-4">
            ✨ <Trans>Photo search is now available</Trans>
          </span>
          <AnimatePresence>
            <motion.button
              layoutId="button"
              layout="position"
              className="btn btn-elevated flex justify-end bg-transparent px-[4px] py-[2px] transition-none"
              onClick={async () => {
                await onJoinWaitlist()
                await setOnboarding.mutateAsync([
                  'smartPhotoSearchBanner',
                  false,
                ])
              }}
            >
              <div className="btn btn-tonal right-0 flex min-h-min items-center gap-3 rounded-full p-[6px]">
                <span className="body-sm-sh px-[6px] font-bold">
                  <Trans>Activate</Trans>
                </span>
              </div>
            </motion.button>
          </AnimatePresence>
        </motion.div>
      ) : (
        <motion.div
          layoutId="button"
          transition={{ duration: animationDuration }}
          className="flex items-center gap-3 overflow-hidden rounded-full lg:p-[6px]"
        >
          <motion.span
            className="body-sm-sh rounded-full bg-surface-container px-4 py-2 font-normal transition-none"
            layout="position"
            animate={{ opacity: 1 }}
            transition={{ duration: animationDuration }}
            initial={{ opacity: 0 }}
          >
            Indexing your photos...
          </motion.span>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
